import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { getSearchParam } from 'helpers/getSearchParam';

import { CasinoNewProvider as CasinoProvider, SiteConfigManager } from '@atlas/core';
import { casinoRoutes, COMPONENT_TYPES } from '@atlas/routing';

import Router from 'common/Router/Router';
import history from 'components/MainRouter/history';
import { GROUPS } from 'components/Pages/Casino/constants';
import MainCasinoPage from 'components/Pages/Casino/MainCasinoPage';
import CasinoGameDetails from 'src/components/Pages/Casino/CasinoGameDetails';

import { CasinoFilterProvider } from './CasinoFilterContext';

const getInitialFilterState = () => {
  const categorySearchParam = getSearchParam(window.location.search, 'category');
  const providerSearchParam = getSearchParam(window.location.search, 'providerNames')?.split(',');
  const nameSearchParam = getSearchParam(window.location.search, 'name');

  return {
    filter: {
      categories: categorySearchParam ? [categorySearchParam] : [],
      name: nameSearchParam || '',
      providerNames: providerSearchParam || [],
    },
  };
};

const CasinoRouter = () => {
  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
  const casinoLivePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO_LIVE);

  const {
    location: { pathname, search, state = {} },
  } = useHistory();
  const initialFilterState = getInitialFilterState();

  useEffect(() => {
    if (state?.casinoGameId) {
      const path = pathname.split('/')[1];

      const gameUrl = new URL(
        `${path}/${casinoRoutes.game}/${state.casinoGameId}`,
        location.origin,
      );

      if (state.providerName) {
        gameUrl.searchParams.set('provider', state.providerName);
      }
      gameUrl.searchParams.set('from', `${pathname}${search}`);

      history.replace(gameUrl.pathname + gameUrl.search);
    }
  }, [state?.casinoGameId]);

  const routerRoutes = useMemo(
    () => [
      {
        path: `${casinoPath}/${casinoRoutes.game}/:gameId`,
        render: () => <CasinoGameDetails />,
      },
      {
        path: `${casinoLivePath}/${casinoRoutes.game}/:gameId`,
        render: () => <CasinoGameDetails />,
      },
      {
        path: casinoPath,
        render: () => (
          <CasinoProvider group={GROUPS.CASINO}>
            <CasinoFilterProvider initialFilterState={initialFilterState}>
              <MainCasinoPage />
            </CasinoFilterProvider>
          </CasinoProvider>
        ),
      },
      {
        path: casinoLivePath,
        render: () => (
          <CasinoProvider group={GROUPS.LIVE}>
            <CasinoFilterProvider initialFilterState={initialFilterState}>
              <MainCasinoPage />
            </CasinoFilterProvider>
          </CasinoProvider>
        ),
      },
    ],
    [casinoLivePath, casinoPath, initialFilterState],
  );

  const urlToRedirect = useMemo(() => {
    if (routerRoutes.length === 0) return '/';
    return routerRoutes[0].path;
  }, [routerRoutes]);

  return <Router redirectFrom={casinoPath} redirectTo={urlToRedirect} routes={routerRoutes} />;
};

export default CasinoRouter;
